'use client';

import { AnimatePresence, motion } from 'framer-motion';
import { useAuth } from 'react-oidc-context';
import styled from 'styled-components';

import { AvatarScratch, Logout } from '@zoomit/icons';
import { Flex, Popover, Spacing } from '@zoomit/primitives';
import { Avatar, Button, cssVar, Hr, TextIcon, theme, Typography } from '@zoomit/zoomit-ds';

import { BaseMediaUrl } from '../../constants/apis';
import { useProfileInfo } from '../../hooks/profile/useProfileInfo';
import ProfileLinksDesktop from './ProfileLinksDesktop';
import UserDetails from './UserDetails';
import UserInfo from './UserInfo';

type Props = {
  isTransparent?: boolean;
  setShowLogoutModal(show: boolean): void;
};

const DesktopUserMenu = (props: Props) => {
  const { isAuthenticated, user } = useAuth();

  const {
    data: profileData,
    isSuccess: isProfileDataSuccess,
    isLoading: isProfileDataLoading,
  } = useProfileInfo({
    userId: user?.profile.sub as string,
    enabled: isAuthenticated && !!user?.profile.sub,
  });

  return (
    <Popover
      isHoverable
      placement="bottom-start"
      portalId="profile-button"
      triggerOffset={8}
      content={
        <UserProfileWrapper flexDirection="column" gap={1.8}>
          <UserInfo
            profileData={profileData}
            isProfileDataSuccess={isProfileDataSuccess}
            isProfileDataLoading={isProfileDataLoading}
          />
          <UserDetails />
          <Flex gap={1.2} flexDirection="column">
            <ProfileLinksDesktop isAuthor={!!profileData?.isAuthor} />
            <Hr />
            <LogoutButton variant="text" size="small" onClick={() => props.setShowLogoutModal(true)}>
              <TextIcon rightIcon={<Logout className="!fill-grey1" />}>
                <Typography spacing={{ mr: 'x2' }} lineHeight={1.8}>
                  خروج
                </Typography>
              </TextIcon>
            </LogoutButton>
          </Flex>
        </UserProfileWrapper>
      }
    >
      <AnimatePresence mode={'wait'}>
        <ProfileButton id="profile-button" alignItems="center">
          {profileData?.avatar ? (
            <motion.div key={'user-avatar'} initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <Avatar size="small" src={`${BaseMediaUrl}/${profileData.avatar}`} />
            </motion.div>
          ) : (
            <motion.div key={'default-avatar'} exit={{ opacity: 0 }}>
              <AvatarScratch
                size="lg"
                fill={cssVar(props.isTransparent ? theme.white : theme.text)}
                stroke={cssVar(props.isTransparent ? theme.white : theme.text)}
              />
            </motion.div>
          )}
        </ProfileButton>
      </AnimatePresence>
    </Popover>
  );
};

export default DesktopUserMenu;

const ProfileButton = styled(Flex)`
  position: relative;
  cursor: pointer;
`;

const UserProfileWrapper = styled(Flex)`
  width: 33rem;
  background-color: var(${theme.card});
  box-shadow: 0 4px 10px 0 rgba(50, 50, 60, 0.2);
  padding: ${Spacing.x6}rem;
  border-radius: var(${theme.radius.md});
  cursor: auto;
`;

const LogoutButton = styled(Button)`
  padding: 0;
  height: auto;
  width: fit-content;
`;
